import { ChangeDetectorRef, Component, DestroyRef, OnInit, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartData, ChartConfiguration } from 'chart.js';

import { AuthService } from 'src/app/_service/auth.service';
import { ClaimService } from 'src/app/_service/claim.service';
import { CompanyService } from 'src/app/_service/company.service';
import { VesselService } from 'src/app/_service/vessel.service';
import { UserService } from 'src/app/_service/user.service';
import { getCompanyData } from 'src/app/_model/company';
import { UserData } from 'src/app/_model/user_data';
import { getVesselData } from 'src/app/_model/vessel';
import { STATUS } from 'src/app/_model/string_literal';

import * as routing from '../../_model/routing';
import { ClaimNewComponent } from './claim-new/claim-new.component';
import { StreamingHttpUtil } from 'src/app/_common/common.streaminghttp';
import { takeUntil } from 'rxjs';

type TagMatchMode = "any" | "all";
@Component({
  selector: 'app-claim-center',
  templateUrl: './claim-center.component.html',
  styleUrl: './claim-center.component.css',
})
export class ClaimCenterComponent implements OnInit {
  myUserName: string;
  myUserId: string;
  allClaims: any[] = [];
  searchTitle: string = "";
  searchVesselName: string = "";
  searchShipyard: string = "";
  searchVesselNo: string = "";
  searchCsNumber: string = "";
  incidentDateRange: Date[] | null = null;
  isSearching: boolean = false;
  searchResults: any[] = [];
  filteredSearchResults: any[] = [];

  searchStatuses: string[] = [];
  searchPriorities: number[] = [];
  searchConfidential: boolean[] = [];

  allClaimsStatistics: number[] = [];
  myClaimsStatistics = [];

  allClaimsSummary = {
    New: 0,
    Open: 0,
    Resolved: 0,
    Closed: 0,
  };
  myClaimsSummary = {
    New: 0,
    Open: 0,
    Resolved: 0,
    Closed: 0,
  };

  myClaimsData: ChartData<"doughnut"> = {
    labels: [],
    datasets: [],
  };
  allClaimsData: ChartData<"doughnut"> = {
    labels: [],
    datasets: [],
  };
  myTotalTickets = 0;
  teamTotalTickets = 0;

  get companyList(): getCompanyData[] {
    return this.companyService.companyList ?? [];
  }
  get userList(): UserData[] {
    return this.userService.userList ?? [];
  }
  get vesselList(): getVesselData[] {
    return this.vesselService.vesselList ?? [];
  }

  readonly PAGE_SIZE = 20;

  offset_new = 0;
  isLoading = false;
  hasMore = true;
  hasLoadedOnce = false;

  searchMark: number | null = null;
  searchTags: string[] = [];
  searchTagMatchMode: TagMatchMode = "all";

  markSearchOptions = [
    {
      id: 4,
      name: "advice",
    },
    {
      id: 0,
      name: "guarantee",
    },
  ];

  tagMatchOptions = [
    {
      value: "all" as TagMatchMode,
      translationKey: "matchAll",
    },
    {
      value: "any" as TagMatchMode,
      translationKey: "matchAny",
    },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
    public auth: AuthService,
    private claimService: ClaimService,
    private companyService: CompanyService,
    private vesselService: VesselService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.myUserName = this.auth.userName;
    this.myUserId = this.auth.userId;
  }

  ngOnInit(): void {
    this.loadMoreClaims();
    this.cdr.markForCheck();

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params.get('dialog') === "create-claim") {
          this.openCreateClaim();
        }
      })
  }

  refreshClaimsList() {
    this.allClaims = [];
    this.searchResults = [];
    this.filteredSearchResults = [];
    this.offset_new = 0;
    this.hasMore = true;
    this.isLoading = false;
    this.hasLoadedOnce = false;

    if (this.isSearching) {
      this.searchClaim();
    } else {
      this.loadMoreClaims();
    }

    this.cdr.markForCheck();
  }

  private normalizeClaimsResponse(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && Array.isArray(value.data)) {
      return value.data;
    }

    if (value && Array.isArray(value.items)) {
      return value.items;
    }

    if (value && Array.isArray(value.claims)) {
      return value.claims;
    }

    return [];
  }

  loadMoreClaims() {
    if (this.isLoading || !this.hasMore) {
      return;
    }
    this.isLoading = true;

    this.claimService.getClaimsByPage(this.offset_new, this.PAGE_SIZE).subscribe({
      next: async (value: any) => {
        const nextClaims = this.normalizeClaimsResponse(value);
        this.allClaims.push(...nextClaims);

        this.offset_new += nextClaims.length;
        this.hasLoadedOnce = true;
        this.cdr.markForCheck();

        if (nextClaims.length < this.PAGE_SIZE) {
          this.hasMore = false;
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching claims:', err);
        this.isLoading = false;
        this.hasMore = false;
        this.hasLoadedOnce = true;
        this.cdr.markForCheck();
      }
    });
  }

  onScroll(event: any) {
    const element = event.target as HTMLElement;

    const threshold = 100;

    const reachedBottom = 
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - threshold;
    
    if (reachedBottom) {
      this.loadMoreClaims();
    }
  }

  // summarizeClaims() {
  //   this.claimService.getClaimStats().subscribe({
  //     next: async (value: any) => {
  //       this.allClaimsSummary = value;
  //       type ClaimStatusKey = 'New' | 'Open' | 'Resolved' | 'Closed';
  //       const order: ClaimStatusKey[] = ['New', 'Open', 'Resolved', 'Closed'];
  //       this.allClaimsStatistics = order.map(key => this.allClaimsSummary[key]);
  //       this.makeChartData();
  //     }
  //   });
  //   ({ claimsSummary: this.myClaimsSummary, summaryArray: this.myClaimsStatistics } = this.getStatistics(this.myClaims));
  //   this.makeChartData();
  // }

  // getStatistics(claims: any): any {
  //   type ClaimStatusKey = 'New' | 'Open' | 'Resolved' | 'Closed';
  //   const claimsSummary = {
  //     New: 0,
  //     Open: 0,
  //     Resolved: 0,
  //     Closed: 0,
  //   };

  //   claims.forEach((claim: any) => {
  //     const { status, assignee, } = claim;
  //     if (status === STATUS.RESOLVED)
  //       claimsSummary.Resolved++;
  //     else if (status === STATUS.NEW) {
  //       if (assignee === null)
  //         claimsSummary.New++;
  //       else
  //         claimsSummary.Open++;
  //     } else if (status === STATUS.CLOSED)
  //       claimsSummary.Closed++;
  //   });
    
  //   const order: ClaimStatusKey[] = ['New', 'Open', 'Resolved', 'Closed'];
  //   const summaryArray = order.map(key => claimsSummary[key]);
  //   return { claimsSummary, summaryArray };
  // }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
  
    return `${year}-${month}-${day}`;
  }

  getDate(date: string) {
    if (!date) {
      return ''
    }
    return date.slice(0, 10)
  }

  searchClaim() {
    this.searchResults = [];
    this.filteredSearchResults = [];
    const fromDate = this.incidentDateRange?.[0]
      ? this.formatDate(this.incidentDateRange[0])
      : '';

    const toDate = this.incidentDateRange?.[1]
      ? this.formatDate(this.incidentDateRange[1])
      : '';

    const normalizedTags = [
      ...new Set(
        this.searchTags
          .map((tag) => tag.trim())
          .filter(Boolean)
      ),
    ];

    if (!this.canSearch) {
      this.isSearching = false;
      return;
    }

    const confidential = this.searchConfidential.length === 1 ? this.searchConfidential[0] : null;

    this.claimService.searchClaims(
      this.searchTitle,
      "",
      this.searchShipyard,
      this.searchVesselName,
      this.searchVesselNo,
      fromDate,
      toDate,
      this.searchMark,
      normalizedTags.join(","),
      this.searchTagMatchMode,
      this.searchCsNumber,
      'claim',
      this.searchStatuses,
      this.searchPriorities,
      confidential
    ).subscribe({
      next: async (value: any) => {
        this.searchResults = this.normalizeClaimsResponse(value);
        this.filteredSearchResults = [...this.searchResults];
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading claims for search:', err);
      }
    });

    this.isSearching = true;
  }

  private normalizeText(value: any): string {
    return String(value ?? '').trim().toLocaleLowerCase();
  }

  matchesClaimSearchFilter(claim: any, rawFilter: string): boolean {
    const filter = this.normalizeText(rawFilter);
    if (!filter) {
      return true;
    }

    const readableId = this.normalizeText(claim?.readableId || claim?.id);
    return readableId.includes(filter);
  }

  clearSearch() {
    this.searchTitle = "";
    this.searchVesselName = "";
    this.searchShipyard = "";
    this.searchVesselNo = "";
    this.searchCsNumber = "";
    this.incidentDateRange = null;
    this.searchMark = null;
    this.searchTags = [];
    this.searchTagMatchMode = "any";

    this.searchResults = [];
    this.isSearching = false;
    this.searchStatuses = [];
    this.searchPriorities = [];
    this.searchConfidential = [];
    this.filteredSearchResults = [];
  }

  openCreateClaim() {
    const dialogRef = this.dialog.open(ClaimNewComponent, {
      width: '50vw',
      position: { right: '0', top: '60px' },
    });

    dialogRef.afterClosed().subscribe((created: boolean) => {
      if (created) {
        this.refreshClaimsList();
      }
      if (this.route.snapshot.queryParamMap.has('dialog')) {
        this.router.navigate([], {
          queryParams: { dialog: null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  trackByClaimId(index: number, claim: any) {
    return claim?.id || claim?.readableId || index;
  }

  getUserName(userId: string) {
    const name = this.userList.find(u => u.id === userId)?.name || "";
    return userId === this.myUserId ? `${name} (me)` : name;
  }

  getCompanyName(companyId: any) {
    return this.companyList.find(c => c.id === companyId)?.name || this.companyList.find(c => c.id === companyId)?.otherName || '';
  }

  getVesselName(vesselId: string) {
    return this.vesselList.find(v => v.id === vesselId)?.name || this.vesselList.find(v => v.id === vesselId)?.otherName || '';
  }

  getDispStatus(claim: any) {
    if (claim.status === STATUS.NEW) {
      if (claim.assignee === null)
        return 'new';
      return 'open'
    }
    if (claim.status === STATUS.CLOSED)
      return 'closed';
    if (claim.status === STATUS.RESOLVED)
      return 'resolved';
    return '';
  }

  getConfidential(isConfidential: number) {
    if (isConfidential) {
      return '●';
    }
    return '';
  }

  getVesselBuiltYear(vesselId: string) {
    return this.vesselList.find(v => v.id === vesselId)?.builtYear || '';
  }

  getUserCompany(userId: string) {
    const companyId = this.userList.find(u => u.id === userId)?.company_id;
    return this.getCompanyName(companyId);
  }
  
  // makeChartData() {
  //   this.allClaimsData = {
  //     labels: ['New', 'Open', 'Resolved', 'Closed'],
  //     datasets: [{
  //       data: this.allClaimsStatistics,
  //       backgroundColor: [
  //         '#1976d2', '#f57c00', '#388e3c', '#616161'
  //       ],
  //       borderColor: "transparent",
  //     }]
  //   };
  
  //   this.myClaimsData = {
  //     labels: ['New', 'Open', 'Resolved', 'Closed'],
  //     datasets: [{
  //       data: this.myClaimsStatistics,
  //       backgroundColor: [
  //         '#1976d2', '#f57c00', '#388e3c', '#616161'
  //       ],
  //       borderColor: "transparent",
  //     }]
  //   };
  
  //   this.teamTotalTickets = this.allClaimsStatistics.reduce((acc, curr) => acc + curr, 0);
  //   this.myTotalTickets = this.myClaimsStatistics.reduce((acc, curr) => acc + curr, 0);
  // }

  chartConfig: ChartConfiguration<"doughnut">['options'] = {
    cutout: "90%",
    radius: "100%",
    plugins: {
      legend: {
        display: false,
      }
    }
  }

  getPriorityText(priority: number) {
    switch(priority) {
      case 1:
        return 'High';
      case 2:
        return 'Medium';
      case 3:
        return 'Low';
    }
    return;
  }

  editClaim(claim: any) {
    const claimsToUse = this.isSearching ? this.filteredSearchResults : this.allClaims;
    this.claimService.setClaimNavigationContext(claimsToUse, claim);
    this.claimService.selectedClaim = claim;
    const readableId = claim?.readableId || claim?.id;
    if (!readableId) {
      console.error('Claim is missing a readable id.', claim);
      return;
    }

    this.router.navigate(['/dashboard/claim-center/claim-edit', readableId]);
  }
  
  getVesselBuilder(id: string) {
    const builderId = this.vesselList.find(v => v.id === id)?.builder_id;
    const builderName = this.companyList.find(c => c.id === builderId)?.name || this.companyList.find(c => c.id === builderId)?.otherName;
    return builderName || '';
  }

  getVesselNo(id: string) {
    return this.vesselList.find(v => v.id === id)?.vesselNumber || '';
  }

  get canSearch() : boolean {
    return  !!this.searchCsNumber?.toString().trim()
      ||    !!this.searchVesselNo?.toString().trim()
      ||    !!this.searchShipyard?.toString().trim()
      ||    !!this.searchTitle?.toString().trim()
      ||    !!this.searchVesselName?.toString().trim()
      ||    !!this.incidentDateRange?.length
      ||    this.searchMark !== null
      ||    this.searchTags.length > 0
      ||    this.searchStatuses.length > 0
      ||    this.searchPriorities.length > 0
      ||    this.searchConfidential.length > 0
  }

  get selectedFilterCount(): number {
    return this.searchStatuses.length + this.searchPriorities.length + this.searchConfidential.length;
  }

  get selectedFilterTags(): { type: 'status' | 'priority' | 'confidential'; value: string | number | boolean; label: string }[] {
    const priorityLabels: Record<number, string> = { 1: 'high', 2: 'medium', 3: 'low' };
    return [
      ...this.searchStatuses.map(value => ({ type: 'status' as const, value, label: value })),
      ...this.searchPriorities.map(value => ({ type: 'priority' as const, value, label: priorityLabels[value] })),
      ...this.searchConfidential.map(value => ({ type: 'confidential' as const, value, label: value ? 'yes' : 'no' })),
    ];
  }

  trackByFilterTag(index: number, tag: { type: string; value: string | number | boolean }): string {
    return `${tag.type}:${tag.value}`;
  }

  removeSearchFilter(type: 'status' | 'priority' | 'confidential', value: string | number | boolean): void {
    switch (type) {
      case 'status':
        this.searchStatuses = this.searchStatuses.filter(item => item !== value);
        break;
      case 'priority':
        this.searchPriorities = this.searchPriorities.filter(item => item !== value);
        break;
      case 'confidential':
        this.searchConfidential = this.searchConfidential.filter(item => item !== value);
        break;
    }
  }

  closeFiltersOnOutsideClick(event: MouseEvent, details: HTMLDetailsElement): void {
    if (details.open && event.target instanceof Node && !details.contains(event.target)) {
      details.open = false;
    }
  }
}